import React, { useMemo, useRef, useEffect } from "react";
import { X, Clock, AlertTriangle } from "lucide-react";
import { daysDiff, todayStr } from "@/utils";

/**
 * Notifications dropdown anchored to the bell button.
 * Shows overdue + due-today tasks, click to open task editor.
 */
export const NotificationsPanel = ({ open, onClose, tasks, onEditTask }) => {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                // ignore clicks on the bell trigger itself (handled by App)
                if (!e.target.closest?.("[data-testid='notifications-btn']")) {
                    onClose();
                }
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, onClose]);

    const { overdue, today, upcoming } = useMemo(() => {
        const today_ = todayStr();
        const active = tasks.filter((t) => !t.completed && !t.archived);
        const overdue_ = active.filter((t) => t.date && t.date < today_);
        const today_list = active.filter((t) => t.date === today_);
        const upcoming_ = active
            .filter((t) => t.date && t.date > today_)
            .sort((a, b) => daysDiff(a.date) - daysDiff(b.date))
            .slice(0, 5);
        return { overdue: overdue_, today: today_list, upcoming: upcoming_ };
    }, [tasks]);

    if (!open) return null;

    const total = overdue.length + today.length + upcoming.length;

    const Row = ({ task, tone }) => (
        <button
            onClick={() => {
                onEditTask(task);
                onClose();
            }}
            className="w-full text-left flex items-start gap-2 px-3 py-2 hover:bg-white/[0.03] border-b border-meye-border/40 last:border-b-0"
            data-testid={`notification-item-${task.id}`}
        >
            <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${tone}`} />
            <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-100 truncate">{task.text}</p>
                <p className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    {task.date} {task.time ? `· ${task.time}` : ""}
                </p>
            </div>
        </button>
    );

    return (
        <div
            ref={panelRef}
            className="absolute right-4 top-14 z-40 w-80 max-h-[70vh] overflow-y-auto bg-meye-card border border-meye-border rounded-lg shadow-2xl shadow-black/60"
            data-testid="notifications-panel"
        >
            <div className="flex items-center justify-between px-3 py-2 border-b border-meye-border">
                <span className="font-brand uppercase tracking-widest text-[11px] text-meye-cyan">
                    notifications {total > 0 && `· ${total}`}
                </span>
                <button
                    onClick={onClose}
                    className="text-zinc-500 hover:text-white"
                    data-testid="notifications-close"
                >
                    <X size={14} />
                </button>
            </div>

            {total === 0 && (
                <div className="px-3 py-8 text-center text-zinc-500 text-xs font-mono" data-testid="notifications-empty">
                    // all clear.
                </div>
            )}

            {overdue.length > 0 && (
                <div>
                    <div className="px-3 py-1.5 text-[10px] font-brand font-bold uppercase tracking-widest text-red-400 flex items-center gap-1.5">
                        <AlertTriangle size={11} /> overdue · {overdue.length}
                    </div>
                    {overdue.map((t) => (
                        <Row key={t.id} task={t} tone="bg-red-500" />
                    ))}
                </div>
            )}

            {today.length > 0 && (
                <div>
                    <div className="px-3 py-1.5 text-[10px] font-brand font-bold uppercase tracking-widest text-meye-fuchsia flex items-center gap-1.5">
                        <Clock size={11} /> today · {today.length}
                    </div>
                    {today.map((t) => (
                        <Row key={t.id} task={t} tone="bg-meye-fuchsia" />
                    ))}
                </div>
            )}

            {upcoming.length > 0 && (
                <div>
                    <div className="px-3 py-1.5 text-[10px] font-brand font-bold uppercase tracking-widest text-meye-cyan flex items-center gap-1.5">
                        <Clock size={11} /> upcoming · {upcoming.length}
                    </div>
                    {upcoming.map((t) => (
                        <Row key={t.id} task={t} tone="bg-meye-cyan" />
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationsPanel;
